import assert from 'assert-ts';
import type {ExternalSuggestions} from 'api/types';
import type {Schemas} from 'api/dto.generated';
import {ebsApi} from 'store/ebsApi';
import type {
  DownloadFile,
  FileInfo,
  FullManifestation,
  ManifestationV4,
  WorkV4,
} from './types';
import {
  mapManifestationBaseDto,
  mapManifestationsDto,
  mapToManifestationBaseDto,
  mapWorkBaseDto,
} from './mappers';

const manifestationApi = ebsApi.injectEndpoints({
  endpoints: build => ({
    getManifestation: build.query<
      FullManifestation,
      string,
      Schemas.ManifestationsDto
    >({
      query: (id: string) => ({
        url: 'manifestation/v2/id',
        method: 'POST',
        body: [id],
      }),
      transformResponse: response => {
        return mapManifestationsDto(response)?.[0];
      },
    }),
    getManifestationByEan: build.query<
      FullManifestation,
      string,
      Schemas.ManifestationsDto
    >({
      query: (ean: string) => ({
        url: 'manifestation/v2/ean',
        method: 'POST',
        body: [ean],
      }),
      transformResponse: response => {
        return mapManifestationsDto(response)?.[0];
      },
    }),
    getManifestationByIsbn: build.query<
      FullManifestation,
      string,
      Schemas.ManifestationsDto
    >({
      query: (isbn: string) => ({
        url: 'manifestation/v2/isbn',
        method: 'POST',
        body: [isbn],
      }),
      transformResponse: response => {
        return mapManifestationsDto(response)?.[0];
      },
    }),

    saveManifestation: build.mutation<
      {
        manifestation: ManifestationV4;
        work: WorkV4;
      },
      ManifestationV4,
      Schemas.ManifestationWithExpressionDto
    >({
      query: manifestation => ({
        url: `manifestation/v2/${manifestation.id}`,
        method: 'PUT',
        body: mapToManifestationBaseDto(manifestation),
      }),
      transformResponse: ({expression, ...manifestation}) => ({
        manifestation: mapManifestationBaseDto(manifestation),
        work: mapWorkBaseDto(
          assert(
            expression?.work,
            'putManifestation: expected expression.work',
            expression,
          ),
        ),
      }),
      invalidatesTags: r => (r ? [{type: 'Work', id: r.work.id}] : []),
    }),

    moveManifestationToExistingWork: build.mutation<
      null,
      {manifestationId: string; targetWorkId: string},
      Schemas.MoveManifestationToExistingWorkDto
    >({
      query: ({manifestationId, targetWorkId}) => ({
        url: `manifestation/${manifestationId}/moveToExistingWork`,
        method: 'POST',
        body: {workId: targetWorkId},
      }),
      invalidatesTags: (_, __, {targetWorkId}) =>
        targetWorkId ? [{type: 'Work', id: targetWorkId}] : [],
    }),

    moveManifestationToDuplicateWork: build.mutation<
      string,
      {manifestationId: string; fromWorkId: string},
      Schemas.MoveManifestationToDuplicateWorkResponseDto
    >({
      query: ({manifestationId, fromWorkId}) => ({
        url: `manifestation/${manifestationId}/moveToDuplicateWork`,
        method: 'POST',
        body: {workId: fromWorkId},
      }),
      transformResponse: r => r.workId!,
      invalidatesTags: (_, __, {fromWorkId}) =>
        fromWorkId ? [{type: 'Work', id: fromWorkId}] : [],
    }),

    deleteManifestation: build.mutation<
      void,
      {manifestationId: string; workId: string},
      Schemas.MoveManifestationToDuplicateWorkResponseDto
    >({
      query: ({manifestationId}) => ({
        url: `manifestation/v2/${manifestationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, {workId}) => [{type: 'Work', id: workId}],
    }),

    getManifestationExternalSuggestions: build.query<
      ExternalSuggestions,
      Pick<ManifestationV4, 'id'>,
      Schemas.ExternalSuggestionsDto
    >({
      keepUnusedDataFor: 60,
      query: ({id}) => `manifestation/${id}/externalSuggestions`,
    }),

    /**
     * Triggered when changerequest is revoked, because backend (Meta) needs us to trigger an outbox update.
     * https://bokbasen.jira.com/browse/BB-6488
     */
    triggerManifestationOutbox: build.mutation<
      void,
      Pick<ManifestationV4, 'id'>,
      void
    >({
      query: ({id}) => ({
        url: `manifestation/v2/${id}/outbox`,
        method: 'POST',
      }),
    }),

    getManifestationFileInfo: build.query<
      FileInfo,
      Pick<ManifestationV4, 'ean'>,
      Schemas.FileInfoDto
    >({
      keepUnusedDataFor: 60,
      query: ({ean}) => `manifestation/${ean}/fileinfo`,
    }),

    getManifestationDownloadFileInfo: build.query<
      DownloadFile,
      Pick<ManifestationV4, 'ean'>,
      Schemas.DownloadFileDto
    >({
      query: ({ean}) => `manifestation/${ean}/download`,
    }),
  }),
});

export const {
  useSaveManifestationMutation,
  useLazyGetManifestationQuery,
  useLazyGetManifestationByEanQuery,
  useLazyGetManifestationByIsbnQuery,
  useMoveManifestationToExistingWorkMutation,
  useMoveManifestationToDuplicateWorkMutation,
  useDeleteManifestationMutation,
  useLazyGetManifestationExternalSuggestionsQuery,
  useTriggerManifestationOutboxMutation,
  useGetManifestationFileInfoQuery,
  useLazyGetManifestationDownloadFileInfoQuery,
} = manifestationApi;
